import { t } from '../i18n';

export type ThemeMode = 'night' | 'day' | 'system';

const STORAGE_KEY = 'mylib-theme';

/** Built per render: a module-level list would freeze the language of its labels. */
export function themeOptions(): { value: ThemeMode; label: string; hint: string }[] {
  return [
    { value: 'night', label: t('theme.night'), hint: t('theme.nightHint') },
    { value: 'day', label: t('theme.day'), hint: t('theme.dayHint') },
    { value: 'system', label: t('theme.system'), hint: t('theme.systemHint') },
  ];
}

export function readTheme(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'day' || stored === 'system' ? stored : 'night';
}

export function resolvedTheme(mode: ThemeMode): 'night' | 'day' {
  if (mode !== 'system') return mode;
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'day' : 'night';
}

export function applyTheme(mode: ThemeMode): void {
  document.documentElement.dataset.theme = resolvedTheme(mode);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolvedTheme(mode) === 'day' ? '#f3f2f8' : '#141824');
}

export function writeTheme(mode: ThemeMode): void {
  localStorage.setItem(STORAGE_KEY, mode);
  applyTheme(mode);
}

/** Keeps the 'system' choice honest while the tab stays open. */
export function followSystemTheme(): () => void {
  const media = window.matchMedia('(prefers-color-scheme: light)');
  const onChange = () => {
    if (readTheme() === 'system') applyTheme('system');
  };
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}
